// filepath: c:\Users\Salim\Documents\Salim\Personal\Projects\K & K\CFMS\project-root\frontend\src\components\admin\Staffs.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import useSortableTable from '../common/useSortableTable';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext';
import StaffForm from './StaffForm';

const STAFF_API = '/api/admin/staff/';
const DEPT_API = '/api/admin/departments/';

const authHeaders = (json = true) => {
  const token = localStorage.getItem('kk_token') || localStorage.getItem('token');
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

export default function Staffs() {
  const { hasPermission } = usePermissions();
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  // Modal State
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedStaff, setSelectedStaff] = useState(null);

  const fetchDeps = useCallback(async () => {
    const res = await fetch(`${DEPT_API}?active=true`, { headers: authHeaders(false) });
    if (res.ok) setDepartments(await res.json());
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      if (deptFilter) params.append('department', deptFilter);
      if (activeFilter !== '') params.append('active', activeFilter);
      const res = await fetch(`${STAFF_API}?${params.toString()}`, { headers: authHeaders(false) });
      if (res.ok) setRows(await res.json());
    } finally { setLoading(false); }
  }, [search, deptFilter, activeFilter]);

  useEffect(() => {
    if (hasPermission('STAFFS', 'can_view')) {
      fetchDeps();
    }
  }, [fetchDeps, hasPermission]);

  useEffect(() => {
    if (hasPermission('STAFFS', 'can_view')) {
      fetchRows();
    }
  }, [fetchRows, hasPermission]);

  const remove = async id => {
    if (!hasPermission('STAFFS', 'can_delete')) {
      toast.error("You do not have permission to delete staff.");
      return;
    }
    if (!window.confirm('Delete staff?')) return;
    await fetch(`${STAFF_API}${id}/`, { method: 'DELETE', headers: authHeaders(false) });
    fetchRows();
  };

  const deptOptions = useMemo(
    () => departments.map(d => ({ id: d.id, text: d.name })),
    [departments]
  );

  // --- Modal Handlers ---
  const openForm = (mode, staffData = null) => {
    setFormMode(mode);
    setSelectedStaff(staffData);
    setShowFormModal(true);
  };

  const handleFormClose = (refreshList = false) => {
    setShowFormModal(false);
    setSelectedStaff(null);
    setFormMode('add');
    if (refreshList) {
        fetchRows();
    }
  };

  // Use sortable table hook
  const { items: sortedRows, requestSort, getSortIndicator } = useSortableTable(rows);

  if (!hasPermission('STAFFS', 'can_view')) {
    return (
        <div className="staff-list-container">
            <h2>Permission Denied</h2>
            <p>You do not have permission to view this page.</p>
        </div>
    );
  }

  return (
    <div className="staff-list-container">
      <h2>Staffs</h2>
      
      {/* Toolbar matching CustomerList structure */}
      <div className="toolbar">
        <div className="filters-group" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
                type="text"
                placeholder="Search staff..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '250px' }}
            />
            <div style={{ width: '200px' }}>
                <Select2Dropdown
                    options={[{ id: '', text: 'All Departments' }, ...deptOptions]}
                    value={deptFilter}
                    onChange={setDeptFilter}
                    valueKey="id"
                    displayKey="text"
                    placeholder="Department"
                />
            </div>
            <div style={{ width: '150px' }}>
                <Select2Dropdown
                    options={[
                        { id: '', text: 'All Status' },
                        { id: 'true', text: 'Active' },
                        { id: 'false', text: 'Inactive' }
                    ]}
                    value={activeFilter}
                    onChange={setActiveFilter}
                    valueKey="id"
                    displayKey="text"
                    placeholder="Status"
                />
            </div>
        </div>

        {/* Add New Button */}
        {hasPermission('STAFFS', 'can_create') && (
            <button onClick={() => openForm('add')}>
                + Add New Staff
            </button>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th onClick={() => requestSort('id')} style={{cursor: 'pointer'}}>ID {getSortIndicator('id')}</th>
              <th onClick={() => requestSort('name')} style={{cursor: 'pointer'}}>Name {getSortIndicator('name')}</th>
              <th onClick={() => requestSort('email')} style={{cursor: 'pointer'}}>Email {getSortIndicator('email')}</th>
              <th onClick={() => requestSort('department_name')} style={{cursor: 'pointer'}}>Department {getSortIndicator('department_name')}</th>
              <th onClick={() => requestSort('phone')} style={{cursor: 'pointer'}}>Phone {getSortIndicator('phone')}</th>
              <th onClick={() => requestSort('position')} style={{cursor: 'pointer'}}>Position {getSortIndicator('position')}</th>
              <th onClick={() => requestSort('active')} style={{cursor: 'pointer'}}>Active {getSortIndicator('active')}</th>
              <th onClick={() => requestSort('joining_date')} style={{cursor: 'pointer'}}>Joining {getSortIndicator('joining_date')}</th>
              {(hasPermission('STAFFS', 'can_edit') || hasPermission('STAFFS', 'can_delete')) && (
                <th>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} style={{ textAlign: 'center' }}>Loading...</td></tr>}
            {!loading && sortedRows.length === 0 && <tr><td colSpan={10} className="no-data">No records found.</td></tr>}
            {!loading && sortedRows.map(r => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>{r.name}</td>
                <td>{r.email || '-'}</td>
                <td>{r.department_name || '-'}</td>
                <td>{r.phone || ''}</td>
                <td>{r.position || ''}</td>
                <td>{r.active ? 'YES' : 'NO'}</td>
                <td>{r.joining_date || ''}</td>
                {(hasPermission('STAFFS', 'can_edit') || hasPermission('STAFFS', 'can_delete')) && (
                  <td className="actions">
                    {hasPermission('STAFFS', 'can_view') && (
                        <button 
                            className="icon-btn view-btn" 
                            onClick={() => openForm('view', r)} 
                            title="View"
                        >
                            <i className="fa fa-eye" />
                        </button>
                    )}
                    {hasPermission('STAFFS', 'can_edit') && (
                        <button 
                            className="icon-btn edit-btn" 
                            onClick={() => openForm('edit', r)} 
                            title="Edit"
                        >
                            <i className="fa fa-pencil" />
                        </button>
                    )}
                    {hasPermission('STAFFS', 'can_delete') && (
                        <button 
                            className="icon-btn delete-btn" 
                            onClick={() => remove(r.id)} 
                            title="Delete"
                        >
                            <i className="fa fa-trash" />
                        </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Overlay */}
      {showFormModal && (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '800px', padding: 0, overflow: 'hidden' }}>
                <button className="modal-close-btn" onClick={() => handleFormClose(false)} style={{ zIndex: 10 }}>&times;</button>
                <StaffForm
                    mode={formMode}
                    initialData={selectedStaff}
                    departments={departments}
                    onClose={handleFormClose}
                />
            </div>
        </div>
      )}
    </div>
  );
}